import { BoxProps } from '@chakra-ui/react';
import { noop } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'ui-components';
import {
  LocationPicker,
  useLocationPicker,
} from 'ui-components/dist/LocationPicker';
import { SNACKBAR_ERROR_GENERIC_OPTIONS } from '~/constants';
import { useGetLocationDetails } from '~/hooks/api/useGetLocationDetails';
import { useGetLocationSuggestions } from '~/hooks/api/useGetLocationSuggestions';

import { CreatorLocationDetail, CreatorLocationSuggestion } from './types';
import { getLocationFriendlyName } from './utils';

interface SearchLocationProps extends Omit<BoxProps, 'onError'> {
  creatorLocation: CreatorLocationDetail | null;
  setCreatorLocation: (creatorLocation: CreatorLocationDetail | null) => void;
  onError?: (error?: boolean) => void;
  trackId?: string;
  isSmartData?: boolean;
  inputProps?: {
    clearButtonProps?: {
      'aria-label': string;
      'track-id': string;
    };
  };
}

export const SearchLocationNew: React.FC<SearchLocationProps> = ({
  creatorLocation,
  setCreatorLocation,
  trackId,
  isSmartData,
  inputProps,
  onError = noop,
  ...props
}) => {
  const snackbar = useSnackbar();

  const [locationSearch, setLocationSearch] =
    useState<CreatorLocationSuggestion | null>(null);
  const { data: locationDetails } = useGetLocationDetails(
    locationSearch?.mapbox_id,
  );

  const { getSuggestions } = useGetLocationSuggestions({
    onError: () => snackbar.error(SNACKBAR_ERROR_GENERIC_OPTIONS),
  });

  const handleChange = useCallback(
    (updatedValue: CreatorLocationSuggestion) => {
      setLocationSearch(updatedValue);
      !updatedValue && setCreatorLocation(updatedValue);
    },
    [setLocationSearch, setCreatorLocation],
  );

  const friendlyName = useMemo(
    () => getLocationFriendlyName(creatorLocation),
    [creatorLocation],
  );

  const context = useLocationPicker({
    value: friendlyName,
    onChange: handleChange,
    isSmartData,
    mapBox: {
      // @ts-ignore
      getLocationSuggestions: getSuggestions,
    },
    inputProps: {
      name: 'location',
      trackId,
      isSmartData,
      ...inputProps,
    },
  });
  const { isEmpty, hasAborted, isOpen } = context;

  useEffect(() => {
    !!locationDetails && setCreatorLocation(locationDetails);
  }, [locationDetails, setCreatorLocation]);

  useEffect(() => {
    onError(isEmpty || hasAborted || isOpen);
  }, [isEmpty, hasAborted, isOpen, onError]);

  return <LocationPicker ctx={context} {...props} />;
};
